import React from 'react';

/**
 * Componente de icono de compartir adaptativo por plataforma
 * 
 * Props:
 * - platform: 'auto' | 'ios' | 'android' | 'web' (default: 'auto')
 * - webDefault: 'plane' | 'ios' | 'android' (default: 'plane')
 * - size: number (default: 22)
 * - className: string
 * - style: object
 */

// Función segura para detectar plataforma (compatible con Capacitor)
const getPlatformSafe = () => {
  try {
    // Cuando Capacitor esté disponible, usar su detección
    if (window.Capacitor?.getPlatform) {
      return window.Capacitor.getPlatform(); // 'ios' | 'android' | 'web'
    }
    
    // Fallback: detección por user agent
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) {
      return 'ios';
    }
    if (ua.includes('android')) {
      return 'android';
    }
    
    return 'web';
  } catch {
    return 'web';
  }
};

export function ShareIcon({
  platform = 'auto',
  webDefault = 'plane',
  size = 22,
  className = '',
  style = {},
  ...svgProps
}) {
  const detected = platform === 'auto' ? getPlatformSafe() : platform;

  const commonProps = {
    width: size,
    height: size,
    className,
    style,
    ...svgProps
  };

  if (detected === 'ios') {
    return <IconIOS {...commonProps} />;
  }
  
  if (detected === 'android') {
    return <IconAndroidOutline {...commonProps} />;
  }

  // Web / PWA - elegir icono según webDefault
  if (webDefault === 'ios') {
    return <IconIOS {...commonProps} />;
  }
  if (webDefault === 'android') {
    return <IconAndroidOutline {...commonProps} />;
  }
  
  return <IconPaperPlaneOutline {...commonProps} />;
}

/* ====== ICONOS ====== */

/** iOS-like: cuadrado + flecha arriba (outline) */
export function IconIOS(props) {
  return (
    <svg 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor"
      strokeWidth={2} 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      {...props}
    >
      <polyline points="8 7 12 3 16 7" />
      <line x1="12" y1="3" x2="12" y2="15" />
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
    </svg>
  );
}

/** Android-like: 3 nodos conectados (outline) */
export function IconAndroidOutline(props) {
  return (
    <svg 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor"
      strokeWidth={2} 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      {...props}
    >
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="M8.6 13.5l6.8 4M15.4 6.5L8.6 10.5" />
    </svg>
  );
}

/** Paper plane (outline) — neutral para web */
export function IconPaperPlaneOutline(props) {
  return (
    <svg 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor"
      strokeWidth={2} 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      {...props}
    >
      <path d="M22 2 11 13" />
      <path d="M22 2 14 22l-3-7-7-3 18-10z" />
    </svg>
  );
}

/** Paper plane (fill) — alternativa sólida */
export function IconPaperPlaneFill(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M22 2a1 1 0 0 1 .46 1.89L6.9 11.34l5.13 2.2 2.18 5.13L22.1 2.54A1 1 0 0 1 22 2z" />
      <path d="M13.05 13.05 11 22l-1.72-4.01L5.2 16.28 13.05 13.05z" />
    </svg>
  );
}

export default ShareIcon;

